import { defineStore } from 'pinia';

export const useTestStore = defineStore('testStore', {
  state: () => ({
    antibioticValue: "-1",
    antibioticText: "",
    speciesValue: "",
    speciesText: "",
    estimatedAUC: null,
    measuredCon: null,
    peakConcentration: null,

    predictedFup: null,
    modeAdmin: "IV",
    nbAdminJour: null,
    durationIV: null,
    rowValues: Array(23).fill(0),
    imageUrl: "",
    ecoff: null,
    atbSpeciesPairInfo: [],
    atbDataSheet: [],
    
    // Patient form fields
    patientGender: null,
    patientAge: null,
    patientHeight: null,
    patientWeight: null,
    patientSurfaceArea: null,
    patientCreatinine: null,
    patientCockroft: null,
    patientMDRD: null,
    patientMDRDCorr: null,
    patientCKDEPI: null,
    patientDialysisContinue: null,
    patientDialysisIntermittent: null,
    patientInfectionLocation: "",
    
    // Administration form fields
    adminDailyDose: null,
    adminStartDate: "",
    adminLastAdmin: "",
    adminSampleCollection: "",
    
    // Error handling
    errorMIC: null,
  }),
  getters: {
    // Get the current antibiotic's data row from the sheet
    currentAtbDataRow: (state) => {
      if (!state.atbDataSheet || !state.antibioticText) {
        return null;
      }
      return state.atbDataSheet.find(row => row.ATB === state.antibioticText) || null;
    }
  },
  actions: {
    // Calculate patient metrics
    calculatePatientMetrics() {
      // Calculate body surface area
      if (this.patientHeight && this.patientWeight && this.patientHeight > 0 && this.patientWeight > 0) {
        const result = 0.007184 * (this.patientHeight**(0.725)) * (this.patientWeight**(0.425));
        this.patientSurfaceArea = isFinite(result) ? result.toFixed(2) : null;
      } else {
        this.patientSurfaceArea = null;
      }
      
      // Convert creatinine from µmol/L to mg/dL for calculations
      // 1 mg/dL = 88.42 µmol/L, so µmol/L ÷ 88.42 = mg/dL
      const creatinineMgDl = this.patientCreatinine ? this.patientCreatinine / 88.42 : null;
      
      // Calculate Cockcroft-Gault (result in mL/min)
      if (this.patientAge && this.patientWeight && creatinineMgDl && 
          this.patientAge > 0 && this.patientWeight > 0 && creatinineMgDl > 0) {
        let result = ((140 - this.patientAge) * this.patientWeight) / (72 * creatinineMgDl);
        if (this.patientGender === "female") {
          result *= 0.85;
        }
        this.patientCockroft = isFinite(result) && result > 0 ? result.toFixed(2) : null;
      } else {
        this.patientCockroft = null;
      }
      
      // Calculate MDRD (result in mL/min/1.73m²)
      if (creatinineMgDl && this.patientAge && this.patientGender && 
          creatinineMgDl > 0 && this.patientAge > 0) {
        const result = 186 * ((creatinineMgDl)**(-1.154)) * ((this.patientAge)**(-0.203)) * (this.patientGender === "female" ? 0.742 : 1);
        this.patientMDRD = isFinite(result) && result > 0 ? result.toFixed(2) : null;
      } else {
        this.patientMDRD = null;
      }
      
      // Calculate MDRD adjusted for body surface area
      if (this.patientMDRD && this.patientSurfaceArea && 
          !isNaN(parseFloat(this.patientMDRD)) && !isNaN(parseFloat(this.patientSurfaceArea))) {
        const result = this.patientMDRD * (this.patientSurfaceArea / 1.73);
        this.patientMDRDCorr = isFinite(result) && result > 0 ? result.toFixed(2) : null;
      } else {
        this.patientMDRDCorr = null;
      }
      
      // Calculate CKD-EPI (result in mL/min/1.73m²)
      if (creatinineMgDl && this.patientAge && this.patientGender && 
          creatinineMgDl > 0 && this.patientAge > 0) {
        let result;
        if (this.patientGender === "female") {
          if (creatinineMgDl <= 0.7) {
            result = 144 * ((creatinineMgDl / 0.7)**(-0.329)) * (0.993**(this.patientAge));
          } else {
            result = 144 * ((creatinineMgDl / 0.7)**(-1.209)) * (0.993**(this.patientAge));
          }
        } else {
          if (creatinineMgDl <= 0.9) {
            result = 141 * ((creatinineMgDl / 0.9)**(-0.411)) * (0.993**(this.patientAge));
          } else {
            result = 141 * ((creatinineMgDl / 0.9)**(-1.209)) * (0.993**(this.patientAge));
          }
        }
        this.patientCKDEPI = isFinite(result) && result > 0 ? result.toFixed(2) : null;
      } else {
        this.patientCKDEPI = null;
      }
    }
  }
});